import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  Pagination,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { downloadTicketMessages } from '../../../../helpers/utils/messageExport';
import useGetSavOrdersByOrderId from '../../../../hooks/orders/useGetSavOrdersByOrderId';
import OrderCancelRecap from './OrderCancelRecap.jsx';
import OrderRetractRecap from './OrderRetractRecap.jsx';
import QuoteRecap from './QuoteRecap.jsx';
import RefundRecap from './RefundRecap.jsx';
import SavDraftRecap from './SavDraftRecap.jsx';
import SavOrderRecap from './SavOrderRecap.jsx';
import TicketActivityMessage from './TicketActivityMessage.jsx';
import TicketActivityNote from './TicketActivityNote.jsx';
import TicketActivityPhonecall from './TicketActivityPhonecall.jsx';

const TicketActivity = ({
  ticket,
  tickets,
  setOpenNewTicketDialog,
  setNewTicketDialogMessage,
  mutate,
  mutateTicket,
}) => {
  const orderId = useMemo(() => ticket?.order?._id, [ticket]);
  const { savOrders, isLoading } = useGetSavOrdersByOrderId(orderId);
  const [displaySettings, setDisplaySettings] = useState({
    contactMessages: true,
    serviceProviderMessages: true,
    notes: true,
    phonecalls: true,
    actionOnCommand: true,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const theme = useTheme();

  // reset pagination on state changes
  const handleDisplayChange = useCallback(
    (key) => (_e) => {
      setDisplaySettings((state) => ({
        ...state,
        [key]: !state[key],
      }));
      setCurrentPage(1);
    },
    []
  );

  // Gestionnaire pour le changement de page
  const handlePageChange = useCallback((_event, page) => {
    setCurrentPage(page);
  }, []);

  const memoizedActivities = useMemo(() => {
    const activityList = [];

    if (displaySettings?.contactMessages) {
      ticket?.messages
        ?.filter((message) => message?.recipientType?.code === 'CONTACT')
        .forEach((message) => {
          activityList.push({
            type: 'message',
            data: message,
            date: message.sentAt,
          });
        });
    }

    if (displaySettings?.serviceProviderMessages) {
      ticket?.messages
        ?.filter(
          (message) => message?.recipientType?.code === 'SERVICEPROVIDER'
        )
        .forEach((message) => {
          activityList.push({
            type: 'message',
            data: message,
            date: message.sentAt,
          });
        });
    }

    if (displaySettings?.notes) {
      ticket?.notes?.forEach((note) => {
        if (
          note.isValidated &&
          !ticket?.phonecalls?.some(
            (p) => p?.note?._id?.toString() === note?._id?.toString()
          )
        ) {
          activityList.push({ type: 'note', data: note, date: note.date });
        }
      });
    }

    if (displaySettings?.phonecalls) {
      ticket?.phonecalls?.forEach((phonecall) => {
        activityList.push({
          type: 'phonecall',
          data: phonecall,
          date: phonecall.startedAt,
        });
      });
    }

    if (displaySettings?.actionOnCommand) {
      if (ticket?.refunds?.length > 0) {
        ticket.refunds.forEach((refund) => {
          const isRefundFromCancel =
            ticket?.order?.cancel?.refund?._id === refund?._id;
          const isRefundFromRetraction =
            ticket?.order?.retraction?.refund?._id === refund?._id;

          if (!isRefundFromCancel && !isRefundFromRetraction) {
            activityList.push({
              type: 'refund',
              data: refund,
              date: refund.updatedAt,
            });
          }
        });
      }

      if (ticket?.order?.cancel) {
        activityList.push({
          type: 'cancel',
          data: ticket?.order?.cancel,
          date: ticket?.order?.cancel?.updatedAt,
        });
      }

      if (ticket?.order?.retraction && !ticket?.order?.retraction?.isDraft) {
        activityList.push({
          type: 'retraction',
          data: ticket?.order?.retraction,
          date: ticket?.order?.retraction?.updatedAt,
        });
      }

      // TODO: remove when bov2 is ready
      if (ticket?.order?.activeSavDraft) {
        activityList.push({
          type: 'savDraft',
          data: ticket?.order?.activeSavDraft,
          date: ticket?.order?.activeSavDraft?.updatedAt,
        });
      }

      if (!isLoading && savOrders?.length > 0) {
        savOrders?.forEach((savOrder) => {
          if (savOrder) {
            activityList.push({
              type: 'savOrder',
              data: savOrder,
              date: savOrder?.savDatasFromBO?.created_at,
            });
          }
        });
      }

      if (ticket?.quotes?.some((quote) => quote?.status?.code === 'DRAFT')) {
        ticket?.quotes?.forEach((quote) => {
          if (quote?.status?.code === 'DRAFT') {
            activityList.push({
              type: 'draftQuote',
              data: quote,
              date: quote?.createdAt,
            });
          }
        });
      }

      if (
        ticket?.quotes?.some((quote) => quote?.status?.code === 'VALIDATED')
      ) {
        ticket?.quotes?.forEach((quote) => {
          if (quote?.status?.code === 'VALIDATED') {
            activityList.push({
              type: 'quotes',
              data: quote,
              date: quote?.createdAt,
            });
          }
        });
      }
    }

    activityList.sort((a, b) => new Date(b.date) - new Date(a.date));
    return activityList;
  }, [ticket, displaySettings, isLoading, savOrders]);

  // Calcul des activités paginées
  const paginatedActivities = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return memoizedActivities.slice(startIndex, endIndex);
  }, [memoizedActivities, currentPage, itemsPerPage]);

  // Calcul du nombre total de pages
  const totalPages = useMemo(() => {
    return Math.ceil(memoizedActivities.length / itemsPerPage);
  }, [memoizedActivities.length, itemsPerPage]);

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'auto',
        paddingTop: 1,
        paddingLeft: 1,
        paddingRight: 0.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}
      >
        <FormGroup
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings?.contactMessages}
                onChange={handleDisplayChange('contactMessages')}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: theme.palette.switch.messages.buttonUnchecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.switch.messages.buttonChecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.switch.messages.trackChecked,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.messages.trackUnchecked,
                  },
                }}
              />
            }
            label="MSG Contact"
          />
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings?.serviceProviderMessages}
                onChange={handleDisplayChange('serviceProviderMessages')}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: theme.palette.switch.messages.buttonUnchecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.switch.messages.buttonChecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.switch.messages.trackChecked,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.messages.trackUnchecked,
                  },
                }}
              />
            }
            label="MSG Presta"
          />
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings?.notes}
                onChange={handleDisplayChange('notes')}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: theme.palette.switch.notes.buttonUnchecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.switch.notes.buttonChecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.switch.notes.trackChecked,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: theme.palette.switch.notes.trackUnchecked,
                  },
                }}
              />
            }
            label="Notes"
          />
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings?.phonecalls}
                onChange={handleDisplayChange('phonecalls')}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: theme.palette.switch.phonecalls.buttonUnchecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.switch.phonecalls.buttonChecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.phonecalls.trackChecked,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.phonecalls.trackUnchecked,
                  },
                }}
              />
            }
            label="Appels"
          />
          <FormControlLabel
            control={
              <Switch
                checked={displaySettings?.actionOnCommand}
                onChange={handleDisplayChange('actionOnCommand')}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    color: theme.palette.switch.actionOnCommand.buttonUnchecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.switch.actionOnCommand.buttonChecked,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.actionOnCommand.trackChecked,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor:
                      theme.palette.switch.actionOnCommand.trackUnchecked,
                  },
                }}
              />
            }
            label="Actions / commande"
          />
        </FormGroup>

        {ticket?.messages?.length > 0 && (
          <Tooltip title="Exporter les messages" placement="left">
            <IconButton
              onClick={() => downloadTicketMessages(ticket)}
              size="small"
              sx={{ marginLeft: 1 }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {/* {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            paddingY: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
         
        </Box>
      )} */}

      {/* Informations de pagination */}
      {memoizedActivities.length > 0 && totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 1,
            paddingX: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {memoizedActivities.length} activité
            {memoizedActivities.length > 1 ? 's' : ''} au total
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.875rem',
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} sur {totalPages}
          </Typography>
        </Box>
      )}

      <Box>
        {paginatedActivities?.map((activity) => {
          switch (activity.type) {
            case 'message':
              return (
                <TicketActivityMessage
                  key={activity?.data?._id}
                  message={activity.data}
                  ticket={ticket}
                  tickets={tickets}
                  setOpenNewTicketDialog={setOpenNewTicketDialog}
                  setNewTicketDialogMessage={setNewTicketDialogMessage}
                  mutate={mutate}
                  mutateTicket={mutateTicket}
                />
              );
            case 'note':
              return (
                <TicketActivityNote
                  key={activity?.data?._id}
                  note={activity.data}
                  ticket={ticket}
                />
              );
            case 'phonecall':
              return (
                <TicketActivityPhonecall
                  key={activity?.data?._id}
                  phonecall={activity.data}
                  ticket={ticket}
                  mutate={mutate}
                  mutateTicket={mutateTicket}
                />
              );
            case 'refund':
              return (
                <RefundRecap
                  key={activity?.data?._id}
                  refund={activity?.data}
                  currency={ticket?.order?.currency?.code}
                />
              );
            case 'cancel':
              return (
                <OrderCancelRecap
                  key={activity?.data?._id}
                  cancel={activity?.data}
                  currency={ticket?.order?.currency?.code}
                />
              );
            case 'retraction':
              return (
                <OrderRetractRecap
                  key={activity?.data?._id}
                  retraction={activity?.data}
                  currency={ticket?.order?.currency?.code}
                />
              );
            case 'savDraft':
              return (
                <SavDraftRecap
                  key={activity?.data?._id}
                  savDraft={activity?.data}
                  currency={ticket?.order?.currency?.code}
                />
              );
            case 'savOrder':
              return (
                <SavOrderRecap
                  key={activity?.data?._id}
                  savOrder={activity?.data}
                  currency={ticket?.order?.currency?.code}
                />
              );
            case 'draftQuote':
            case 'quotes':
              return (
                <QuoteRecap
                  key={activity?.data?._id}
                  quote={activity?.data}
                  currency={ticket?.contact?.store?.country?.currency?.code}
                />
              );
            default:
              return null;
          }
        })}
      </Box>
    </Box>
  );
};

export default TicketActivity;
